import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Renders a short translatable string with INLINE markdown only — bold, italic,
 * links (and `del`/`code` from GFM). Block constructs (headings, lists, tables,
 * blockquotes) are stripped: `allowedElements` drops them and `unwrapDisallowed`
 * keeps their text, so e.g. `# Heading` shows the literal word "Heading" with no
 * block element. This mirrors how resource `description` fields are rendered at
 * runtime, so the admin editor preview matches production exactly.
 */
const ALLOWED_ELEMENTS = ["p", "strong", "em", "del", "code", "a"] as const;

const inlineComponents: Components = {
	// react-markdown wraps top-level text in a `p`; render it inline so the
	// output stays on one line and inherits the surrounding text styling.
	p: ({ children }) => <span>{children}</span>,
	a: ({ href, children }) => (
		<a href={href} target="_blank" rel="noopener noreferrer">
			{children}
		</a>
	),
};

interface InlineMarkdownProps {
	children: string;
	className?: string;
}

export const InlineMarkdown = ({
	children,
	className,
}: InlineMarkdownProps) => (
	<span className={className}>
		<ReactMarkdown
			allowedElements={[...ALLOWED_ELEMENTS]}
			unwrapDisallowed
			components={inlineComponents}
			remarkPlugins={[remarkGfm]}
		>
			{children}
		</ReactMarkdown>
	</span>
);
